import React, { FC, useCallback, useState } from "react";
import { Block } from "kanopi-components/Block";
import { Button, SIZE, TButtonProps } from "kanopi-components/Button";
import { mergeOverrides } from "baseui";
import { Modal } from "kanopi-components/Modal";
import { ParagraphSmall } from "kanopi-components/typography";
import { QuoteReferralState } from "kw/api/zeus";
import { Textarea } from "baseui/textarea";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type TAdminQuoteReferralModal = {
  openModal: (arg: boolean) => void;
  title: string;
  modalButtonLabel: string;
  modalText: string;
  newState: QuoteReferralState.DECLINED | QuoteReferralState.ACCEPTED;
  updateQuoteState: (newState: QuoteReferralState, note: string) => void;
};

export const AdminQuoteReferralModal: FC<TAdminQuoteReferralModal> = ({
  openModal,
  title,
  modalButtonLabel,
  modalText,
  newState,
  updateQuoteState,
}) => {
  const [, theme] = useStyletron();
  const [noteContent, setNoteContent] = React.useState("");

  const handleSubmit = useCallback(() => {
    updateQuoteState(newState, noteContent.trim());
    openModal(false);
  }, [noteContent]);

  const onClose = useCallback(() => {
    openModal(false);
  }, []);

  return (
    <Modal onClose={onClose} closeable isOpen={true} animate autoFocus>
      <Modal.Header $style={{ textAlign: "center" }}>{title}</Modal.Header>
      <Modal.Body $style={{ marginTop: theme.sizing.scale800 }}>
        <Block width="100%">
          <ParagraphSmall>{modalText}</ParagraphSmall>
          <Textarea
            id="referralModalNoteInput"
            value={noteContent}
            onChange={(e) => setNoteContent(e.currentTarget.value)}
            placeholder="Add a note (optional)"
          />
        </Block>
      </Modal.Body>
      <Modal.Footer>
        <Button data-testid="referralModalNoteSubmit" onClick={handleSubmit}>
          {modalButtonLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

type TAdminQuoteReferralButton = {
  title: string;
  modalButtonLabel: string;
  modalText: string;
  newState: QuoteReferralState.DECLINED | QuoteReferralState.ACCEPTED;
  updateQuoteState: (newState: QuoteReferralState, note: string) => void;
  color?: string;
} & TButtonProps;

export const AdminQuoteReferralButton: FC<TAdminQuoteReferralButton> = ({
  title,
  modalButtonLabel,
  modalText,
  newState,
  updateQuoteState,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [, theme] = useStyletron();
  const openModal = (open: boolean) => {
    setIsOpen(open);
  };

  const onClick = useCallback(() => {
    openModal(true);
  }, []);
  const { color, overrides, size, ...restProps } = props;

  return (
    <>
      <Button
        size={size || SIZE.compact}
        outlined={true}
        onClick={onClick}
        overrides={mergeOverrides(
          {
            BaseButton: {
              style: {
                color: color,
                outlineColor: color || theme.colors.buttonPrimaryOutlinedBorder,
              },
            },
          },
          overrides
        )}
        {...restProps}
      >
        {title}
      </Button>

      {isOpen && (
        <AdminQuoteReferralModal
          openModal={openModal}
          title={title}
          modalButtonLabel={modalButtonLabel}
          modalText={modalText}
          newState={newState}
          updateQuoteState={updateQuoteState}
        />
      )}
    </>
  );
};
